import api from './api';

// ============================================
// TYPES
// ============================================

export interface ProductReview {
  id: string;
  productId: string;
  productName: string;
  storeId?: string | null;
  storeName: string;
  clientId?: string | null;
  clientName: string;
  orderId?: string | null;
  rating: number;
  title: string;
  comment: string;
  pros: string;
  cons: string;
  images: string[];
  recommends: boolean;
  verified: boolean;
  approved: boolean;
  storeReply: string;
  replyDate?: string;
  helpfulYes: number;
  helpfulNo: number;
  date: string;
}

export interface StoreReview {
  id: string;
  storeId: string;
  storeName: string;
  clientId?: string | null;
  clientName: string;
  orderId?: string | null;
  rating: number;
  serviceRating?: number | null;
  deliveryRating?: number | null;
  qualityRating?: number | null;
  comment: string;
  approved: boolean;
  storeReply: string;
  replyDate?: string;
  date: string;
}

export interface ProductReviewStats {
  total: number;
  approved: number;
  pending: number;
  avgRating: number;
  distribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
}

export interface StoreReviewStats {
  total: number;
  approved: number;
  pending: number;
  avgRating: number;
  avgService: number;
  avgDelivery: number;
  avgQuality: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  needsSetup?: boolean;
}

export interface ProductReviewFilters {
  page?: number;
  limit?: number;
  productId?: number;
  storeId?: number;
  status?: 'approved' | 'pending';
  rating?: number;
  search?: string;
}

export interface StoreReviewFilters {
  page?: number;
  limit?: number;
  storeId?: number;
  status?: 'approved' | 'pending';
  rating?: number;
}

// ============================================
// SERVICE
// ============================================

class ReviewsService {
  // ============================================
  // SETUP
  // ============================================

  async setupTables(): Promise<{
    success: boolean;
    message: string;
    created: string[];
  }> {
    try {
      const response = await api.post('/admin/shop/reviews/setup');
      return response.data;
    } catch (error) {
      console.error('Setup reviews tables error:', error);
      throw error;
    }
  }

  // ============================================
  // PRODUCT REVIEWS
  // ============================================

  async getProductReviews(params?: ProductReviewFilters): Promise<PaginatedResponse<ProductReview>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.productId) queryParams.append('productId', String(params.productId));
      if (params?.storeId) queryParams.append('storeId', String(params.storeId));
      if (params?.status) queryParams.append('status', params.status);
      if (params?.rating) queryParams.append('rating', String(params.rating));
      if (params?.search) queryParams.append('search', params.search);

      const response = await api.get(`/admin/shop/reviews/products?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get product reviews error:', error);
      throw error;
    }
  }

  async getProductReviewStats(storeId?: number, productId?: number): Promise<{ success: boolean; data: ProductReviewStats; needsSetup?: boolean }> {
    try {
      const queryParams = new URLSearchParams();
      if (storeId) queryParams.append('storeId', String(storeId));
      if (productId) queryParams.append('productId', String(productId));

      const response = await api.get(`/admin/shop/reviews/products/stats?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get product review stats error:', error);
      throw error;
    }
  }

  async approveProductReview(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/shop/reviews/products/${id}/approve`);
      return response.data;
    } catch (error) {
      console.error('Approve product review error:', error);
      throw error;
    }
  }

  async rejectProductReview(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/shop/reviews/products/${id}/reject`);
      return response.data;
    } catch (error) {
      console.error('Reject product review error:', error);
      throw error;
    }
  }

  async replyProductReview(id: number, reply: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/shop/reviews/products/${id}/reply`, { reply });
      return response.data;
    } catch (error) {
      console.error('Reply product review error:', error);
      throw error;
    }
  }

  async deleteProductReview(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/admin/shop/reviews/products/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete product review error:', error);
      throw error;
    }
  }

  async bulkApproveProductReviews(ids: number[]): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put('/admin/shop/reviews/products/bulk/approve', { ids });
      return response.data;
    } catch (error) {
      console.error('Bulk approve product reviews error:', error);
      throw error;
    }
  }

  // ============================================
  // STORE REVIEWS
  // ============================================

  async getStoreReviews(params?: StoreReviewFilters): Promise<PaginatedResponse<StoreReview>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.storeId) queryParams.append('storeId', String(params.storeId));
      if (params?.status) queryParams.append('status', params.status);
      if (params?.rating) queryParams.append('rating', String(params.rating));

      const response = await api.get(`/admin/shop/reviews/stores?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get store reviews error:', error);
      throw error;
    }
  }

  async getStoreReviewStats(storeId?: number): Promise<{ success: boolean; data: StoreReviewStats; needsSetup?: boolean }> {
    try {
      const queryParams = storeId ? `?storeId=${storeId}` : '';
      const response = await api.get(`/admin/shop/reviews/stores/stats${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Get store review stats error:', error);
      throw error;
    }
  }

  async approveStoreReview(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/shop/reviews/stores/${id}/approve`);
      return response.data;
    } catch (error) {
      console.error('Approve store review error:', error);
      throw error;
    }
  }

  async replyStoreReview(id: number, reply: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/shop/reviews/stores/${id}/reply`, { reply });
      return response.data;
    } catch (error) {
      console.error('Reply store review error:', error);
      throw error;
    }
  }

  async deleteStoreReview(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/admin/shop/reviews/stores/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete store review error:', error);
      throw error;
    }
  }

  async bulkApproveStoreReviews(ids: number[]): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put('/admin/shop/reviews/stores/bulk/approve', { ids });
      return response.data;
    } catch (error) {
      console.error('Bulk approve store reviews error:', error);
      throw error;
    }
  }

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  getRatingLabel(rating: number): string {
    const labels: Record<number, string> = {
      1: 'Muito Ruim',
      2: 'Ruim',
      3: 'Regular',
      4: 'Bom',
      5: 'Excelente',
    };
    return labels[rating] || String(rating);
  }

  getRatingColor(rating: number): 'error' | 'warning' | 'info' | 'success' {
    if (rating <= 2) return 'error';
    if (rating === 3) return 'warning';
    if (rating === 4) return 'info';
    return 'success';
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  formatDateTime(date: string): string {
    return new Date(date).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}

export default new ReviewsService();
